/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumEdsGradients, CosmosRichTextFragment } from "@cosmos/hygraph-schema";

import {
	CosmosTextVariantEnum,
	CosmosButtonProps,
	CosmosText,
	ButtonList,
	Animate,
	Wrap,
	CosmosDesc,
	CosmosDate,
	CosmosTagProps,
	CosmosTag,
	closeRichTextHeadingGaps,
} from "@components/elements";
import { stripLeadingLineBreaks } from "@components/utils/richText.utils";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

import { styled } from "./ContentLargeImage.MainStack.styles";
import { ColorfulTitle } from "@components/elements/molecules/title-desc-stack/ColorfulTitle.molecule";

interface Props {
	animate?: boolean;
	eyebrow?: string | null;
	buttons?: CosmosButtonProps[] | null;
	gradient?: CosmosEnumEdsGradients | null;
	title?: CosmosRichTextFragment | null;
	titleAs?: "h1" | "h2" | "h3";
	desc?: string | null;
	assetEl?: React.ReactNode;
	badges?: CosmosTagProps[] | null;
	date?: string | null;
}

export const ContentLargeImageMainStack = ({
	animate = false,
	eyebrow,
	buttons = [],
	gradient,
	title,
	titleAs = "h2",
	desc,
	assetEl,
	badges = [],
	date,
}: Props): React.JSX.Element | null => {
	const cleanTitleHtml = stripLeadingLineBreaks(title?.html);
	const titleValid = title?.text && cleanTitleHtml;

	// Authored rich text in `desc` can contain headings (e.g. an intro styled "Heading 3"
	// in Hygraph — see /megagrants). Re-base them one level below the MainStack title so
	// they don't skip from the title's level (an h1 title above a raw authored <h3> jumps
	// h1→h3). aria-level only — the tag and its variant-driven visual size are untouched.
	// No-op when `desc` has no authored headings. When there's no title, keep the authored
	// top level and only close internal gaps.
	const descBaseLevel = titleValid ? Math.min(6, Number(titleAs.slice(1)) + 1) : undefined;

	if (!titleValid && !desc && (!buttons || !buttons.length)) {
		return null;
	}

	const cosmosButtons = (buttons || []).map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
		};
	});

	const hasTopWrap = date || eyebrow;

	return (
		<div className={`content-large-image-main-stack ${styled}`}>
			<VStack alignItems="start" gap="eds.dimension.24">
				{hasTopWrap ? (
					<Wrap condition={animate} wrap={(children) => <Animate type="slide">{children}</Animate>}>
						<VStack gap="eds.dimension.24" alignItems="flex-start">
							{badges?.length || date ? (
								<HStack gap="eds.dimension.16" flexWrap="wrap">
									{badges?.map((badge) => (
										<CosmosTag key={`content-large-image-badge-${badge.label}`} {...badge} />
									))}
									{date ? <CosmosDate date={date} className={css({ color: "eds.text.secondary" })} /> : null}
								</HStack>
							) : null}
							<CosmosText as="span" variant="headingSM" color="eds.text.primary">
								{eyebrow}
							</CosmosText>
						</VStack>
					</Wrap>
				) : null}
				<VStack className={cx("title-desc-buttons", hasTopWrap ? "has-top-wrap" : null)} w="full">
					{titleValid ? (
						<VStack className="title">
							<Wrap
								condition={animate}
								wrap={(children) => (
									<Animate delay={0.05} type="slide">
										{children}
									</Animate>
								)}
							>
								<ColorfulTitle
									className={css({ w: "full" })}
									gradient={gradient}
									title={{
										...title,
										as: titleAs,
										html: cleanTitleHtml,
										variant: CosmosTextVariantEnum.heading2XL,
										color: "eds.text.primary",
									}}
								/>
							</Wrap>
						</VStack>
					) : null}

					{assetEl ? (
						<div
							className={cx(
								"content-large-image-main-stack-image",
								css({
									marginBottom: "eds.dimension.16",
									w: "full",
									md: {
										display: "none",
									},
									sm: {
										marginBottom: "eds.dimension.32",
									},
								}),
							)}
						>
							{assetEl}
						</div>
					) : null}
					<VStack className="desc-buttons">
						{desc ? (
							<Wrap
								condition={animate}
								wrap={(children) => (
									<Animate delay={0.25} type="slide" distance="2rem">
										{children}
									</Animate>
								)}
							>
								<CosmosDesc
									description={{
										html: closeRichTextHeadingGaps(desc, descBaseLevel),
										variant: CosmosTextVariantEnum.paragraphMD,
										color: "eds.text.secondary",
									}}
								/>
							</Wrap>
						) : (
							""
						)}
						<ButtonList
							animate={animate}
							buttons={cosmosButtons}
							className={css({ "& button, & a": { position: "relative", zIndex: 2 } })}
						/>
					</VStack>
				</VStack>
			</VStack>
		</div>
	);
};
